import { useMemo, useState, type ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import { Plus, Pencil } from 'lucide-react';
import {
  useMarkWorkout,
  useWorkoutsInRange,
  type ActualWorkout,
  type PlannedWorkout,
  type WorkoutsResponse,
} from '@/lib/workouts';
import { useCalendarItems, type CalendarApiItem } from '@/lib/calendar';
import { DISCIPLINE_COLORS } from '@/lib/disciplineColors';
import { disciplineLabel } from '@/features/dashboard/WorkoutCard';
import { disciplineEmoji } from '@/features/dashboard/RecentActivitiesCard';
import { PlannedWorkoutForm } from '../PlannedWorkoutForm';
import {
  buildDayRange,
  dayKeyUTC,
  formatDateLong,
  formatTime,
} from './dateUtils';
import { useShowMealsPref } from './showMealsPref';

/**
 * Vista Día: una columna larga con timeline cronológico de actuals + planned
 * sin completar. Cards grandes con todo el detalle relevante. Acciones rápidas
 * en planned: "Marcar como hecho".
 */
export function DayView({ cursor }: { cursor: Date }): ReactElement {
  const { fromISO, toISO } = useMemo(() => buildDayRange(cursor), [cursor]);
  const query = useWorkoutsInRange(fromISO, toISO);
  const dayKey = dayKeyUTC(cursor);
  // M2.46.A — Estado del modal create/edit. `editing` es el planned bajo
  // edición (null = create con la fecha del cursor; undefined = cerrado).
  const [editing, setEditing] = useState<PlannedWorkout | null | undefined>(
    undefined,
  );

  // M2.34.C — Toggle "Mostrar comidas" persistido en localStorage. Si está
  // activo, lanzamos la query a /api/calendar/items y filtramos meal/prep.
  const showMeals = useShowMealsPref();
  const calendarItemsQuery = useCalendarItems(fromISO, toISO, showMeals);

  const actuals = useMemo(
    () => pickActualsForDay(dayKey, query.data ?? null),
    [dayKey, query.data],
  );
  const plannedPending = useMemo(
    () => pickPendingPlannedForDay(dayKey, query.data ?? null),
    [dayKey, query.data],
  );
  const mealsAndPreps = useMemo(() => {
    if (!showMeals || !calendarItemsQuery.data) return [] as CalendarApiItem[];
    return calendarItemsQuery.data.items
      .filter((it) => it.kind === 'meal' || it.kind === 'prep')
      .filter((it) => it.datetime.slice(0, 10) === dayKey)
      .sort((a, b) =>
        a.datetime < b.datetime ? -1 : a.datetime > b.datetime ? 1 : 0,
      );
  }, [showMeals, calendarItemsQuery.data, dayKey]);

  return (
    <section aria-label="Vista del día" data-testid="calendar-day-view">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-slate-900">
          {formatDateLong(cursor)}
        </h2>
        {/* M2.46.A — Botón crear entreno manual. */}
        <button
          type="button"
          onClick={() => setEditing(null)}
          data-testid="calendar-day-add-planned"
          className="inline-flex items-center gap-1.5 rounded border border-teal-600 bg-white px-3 py-1.5 text-xs font-medium text-teal-700 hover:bg-teal-50 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Añadir entreno
        </button>
      </div>

      {query.isError && (
        <p role="alert" className="mt-2 text-sm text-rose-700">
          No se pudieron cargar los entrenos del día.
        </p>
      )}

      {query.isLoading && (
        <p
          role="status"
          aria-live="polite"
          className="mt-3 text-sm text-slate-600"
        >
          Cargando entrenos…
        </p>
      )}

      {!query.isLoading &&
        actuals.length === 0 &&
        plannedPending.length === 0 &&
        mealsAndPreps.length === 0 && (
          <div
            data-testid="calendar-day-empty"
            className="mt-4 rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600"
          >
            Sin entrenos planificados ni completados este día.
          </div>
        )}

      <ol className="mt-4 space-y-3">
        {plannedPending.map((p) => (
          <PlannedItem
            key={`p-${p.id}`}
            planned={p}
            onEdit={() => setEditing(p)}
          />
        ))}
        {actuals.map((a) => (
          <ActualItem
            key={`a-${a.id}`}
            actual={a}
            linkedPlanned={findLinkedPlanned(a.id, query.data ?? null)}
          />
        ))}
        {/* M2.34.C — Comidas + preps intercalados en la timeline. */}
        {mealsAndPreps.map((item) =>
          item.kind === 'meal' ? (
            <MealItem key={item.id} item={item} />
          ) : (
            <PrepItem key={item.id} item={item} />
          ),
        )}
      </ol>
      {/* M2.46.A — Modal de crear/editar entreno. Montado a nivel de vista
          para que viva fuera de la `<ol>` semántica. */}
      {editing !== undefined && (
        <PlannedWorkoutForm
          mode={editing === null ? 'create' : 'edit'}
          {...(editing !== null ? { initial: editing } : {})}
          {...(editing === null ? { defaultDate: cursor } : {})}
          onClose={() => setEditing(undefined)}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// M2.34.C — items meal/prep en la timeline
// ---------------------------------------------------------------------------

function MealItem({ item }: { item: CalendarApiItem }): ReactElement {
  const time = formatTimeFromISO(item.datetime);
  const macros: string[] = [];
  if (item.kcal !== null && item.kcal !== undefined) macros.push(`${item.kcal} kcal`);
  if (item.proteinG !== null && item.proteinG !== undefined)
    macros.push(`${item.proteinG}g prot`);
  if (item.carbsG !== null && item.carbsG !== undefined)
    macros.push(`${item.carbsG}g CHO`);
  if (item.fatG !== null && item.fatG !== undefined) macros.push(`${item.fatG}g grasa`);
  return (
    <li className="flex gap-3" data-testid={`calendar-day-meal-${item.id}`}>
      <div className="w-12 shrink-0 pt-2 text-right text-xs font-medium text-slate-500">
        {time}
      </div>
      <article
        className="flex-1 rounded-lg border border-amber-300 border-l-4 border-l-amber-500 bg-amber-50 p-3 shadow-sm"
        data-kind="meal"
      >
        <header className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-900">
            <span aria-hidden="true" className="text-lg">
              🍽
            </span>
            {item.title}
          </h3>
          {item.mealType && (
            <span className="rounded bg-amber-200 px-2 py-0.5 text-[11px] font-medium text-amber-900">
              {item.mealType}
            </span>
          )}
        </header>
        {macros.length > 0 && (
          <p className="mt-1 text-xs text-amber-800">{macros.join(' · ')}</p>
        )}
        {item.recipeId !== null && item.recipeId !== undefined && (
          <p className="mt-1 text-xs">
            <Link
              to="/nutricion"
              className="text-amber-700 underline hover:text-amber-900"
            >
              Ver receta →
            </Link>
          </p>
        )}
      </article>
    </li>
  );
}

function PrepItem({ item }: { item: CalendarApiItem }): ReactElement {
  const time = formatTimeFromISO(item.datetime);
  return (
    <li className="flex gap-3" data-testid={`calendar-day-prep-${item.id}`}>
      <div className="w-12 shrink-0 pt-2 text-right text-xs font-medium text-slate-500">
        {time}
      </div>
      <article
        className="flex-1 rounded-lg border border-sky-300 border-l-4 border-l-sky-500 bg-sky-50 p-3 shadow-sm"
        data-kind="prep"
      >
        <header className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-sky-900">
            <span aria-hidden="true" className="text-lg">
              ⏳
            </span>
            {item.title}
          </h3>
          <span className="rounded bg-sky-200 px-2 py-0.5 text-[11px] font-medium text-sky-900">
            {Math.round(item.durationMin)} min
          </span>
        </header>
      </article>
    </li>
  );
}

function formatTimeFromISO(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(iso));
  } catch {
    return iso.slice(11, 16);
  }
}

/**
 * M2.29 — Busca el planned que tiene este actualId vinculado para marcar
 * el ActualItem como "Cumplido" / "Parcial" (verde / ámbar).
 */
function findLinkedPlanned(
  actualId: number,
  workouts: WorkoutsResponse | null,
): PlannedWorkout | null {
  if (!workouts) return null;
  return (
    workouts.planned.find((p) => p.actualWorkoutId === actualId) ?? null
  );
}

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

function ActualItem({
  actual,
  linkedPlanned,
}: {
  actual: ActualWorkout;
  linkedPlanned: PlannedWorkout | null;
}): ReactElement {
  const color = DISCIPLINE_COLORS[actual.discipline];
  const minutes = Math.round(actual.durationMin);
  const km =
    typeof actual.distanceM === 'number' && actual.distanceM > 0
      ? actual.distanceM / 1000
      : null;
  const tss = actual.tssComputed ?? actual.tssEstimated ?? null;
  const time = formatTime(actual.datetime, actual.timezone);
  const disc = disciplineLabel(actual.discipline);

  // M2.29 — Si el actual proviene de un planned, marcamos el estado.
  const status = linkedPlanned?.status ?? null;
  const cardClasses =
    status === 'completed'
      ? 'flex-1 rounded-lg border border-emerald-300 border-l-4 bg-emerald-50 p-3 shadow-sm'
      : status === 'partial'
        ? 'flex-1 rounded-lg border border-amber-300 border-l-4 bg-amber-50 p-3 shadow-sm'
        : 'flex-1 rounded-lg border border-slate-200 border-l-4 bg-white p-3 shadow-sm';
  const statusBadge =
    status === 'completed'
      ? { text: '✓ Cumplido', cls: 'bg-emerald-600 text-white' }
      : status === 'partial'
        ? { text: '½ Parcial', cls: 'bg-amber-600 text-white' }
        : null;

  return (
    <li className="flex gap-3" data-testid={`calendar-day-actual-${actual.id}`}>
      <div className="w-12 shrink-0 pt-2 text-right text-xs font-medium text-slate-500">
        {time}
      </div>
      <article
        style={{ borderLeftColor: color.hex }}
        className={cardClasses}
        data-status={status ?? 'unplanned'}
      >
        <header className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <span aria-hidden="true" className="text-lg">
              {disciplineEmoji(actual.discipline)}
            </span>
            {disc}
            {statusBadge && (
              <span
                className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusBadge.cls}`}
              >
                {statusBadge.text}
              </span>
            )}
          </h3>
          <span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
            {minutes} min
          </span>
        </header>
        <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-700 sm:grid-cols-4">
          {km !== null && (
            <div>
              <dt className="text-[10px] uppercase text-slate-500">Distancia</dt>
              <dd className="font-medium text-slate-900">{formatKm(km)} km</dd>
            </div>
          )}
          {actual.avgHr !== null && actual.avgHr !== undefined && (
            <div>
              <dt className="text-[10px] uppercase text-slate-500">HR media</dt>
              <dd className="font-medium text-slate-900">{actual.avgHr} bpm</dd>
            </div>
          )}
          {actual.avgPower !== null && actual.avgPower !== undefined && (
            <div>
              <dt className="text-[10px] uppercase text-slate-500">Potencia</dt>
              <dd className="font-medium text-slate-900">{actual.avgPower} W</dd>
            </div>
          )}
          {tss !== null && (
            <div>
              <dt className="text-[10px] uppercase text-slate-500">TSS</dt>
              <dd className="font-medium text-slate-900">{Math.round(tss)}</dd>
            </div>
          )}
        </dl>
        <div className="mt-2">
          <Link
            to="/entreno/$id"
            params={{ id: String(actual.id) }}
            className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:text-teal-900 hover:underline focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
          >
            Ver detalle <span aria-hidden="true">→</span>
          </Link>
        </div>
      </article>
    </li>
  );
}

function PlannedItem({
  planned,
  onEdit,
}: {
  planned: PlannedWorkout;
  onEdit: () => void;
}): ReactElement {
  const mark = useMarkWorkout();
  const color = DISCIPLINE_COLORS[planned.discipline];
  const minutes = Math.round(planned.durationPlannedMin);
  const time = formatTime(planned.datetime, planned.timezone);
  const disc = disciplineLabel(planned.discipline);

  return (
    <li className="flex gap-3" data-testid={`calendar-day-planned-${planned.id}`}>
      <div className="w-12 shrink-0 pt-2 text-right text-xs font-medium text-slate-500">
        {time}
      </div>
      <article
        style={{ borderLeftColor: color.hex }}
        className="flex-1 rounded-lg border border-teal-300 border-l-4 bg-teal-50 p-3 shadow-sm"
      >
        <header className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-teal-900">
            <span aria-hidden="true" className="text-lg">
              {disciplineEmoji(planned.discipline)}
            </span>
            Planificado · {disc}
          </h3>
          <span className="rounded bg-teal-700 px-2 py-0.5 text-[11px] font-medium text-white">
            {minutes} min
          </span>
        </header>
        {planned.intensityZone && (
          <p className="mt-1 text-xs text-teal-800">Zona: {planned.intensityZone}</p>
        )}
        {planned.description && (
          <p className="mt-1 whitespace-pre-wrap text-xs text-teal-900">
            {planned.description}
          </p>
        )}
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => mark.mutate({ id: planned.id, status: 'completed' })}
            disabled={mark.isPending}
            className="inline-flex items-center gap-1 rounded border border-teal-600 bg-white px-2.5 py-1 text-xs font-medium text-teal-700 hover:bg-teal-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
            aria-label={`Marcar ${minutes} min de ${disc} como hecho`}
          >
            <span aria-hidden="true">✓</span>
            Marcar como hecho
          </button>
          {/* M2.46.A — Editar/borrar el planned. */}
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-1"
            aria-label={`Editar entreno ${disc}`}
          >
            <Pencil className="h-3 w-3" aria-hidden="true" />
            Editar
          </button>
        </div>
      </article>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function pickActualsForDay(
  dayKey: string,
  workouts: WorkoutsResponse | null,
): ActualWorkout[] {
  if (!workouts) return [];
  return workouts.actual
    .filter((a) => isOnDay(a.datetime, dayKey))
    .sort((a, b) => (a.datetime < b.datetime ? -1 : a.datetime > b.datetime ? 1 : 0));
}

function pickPendingPlannedForDay(
  dayKey: string,
  workouts: WorkoutsResponse | null,
): PlannedWorkout[] {
  if (!workouts) return [];
  return workouts.planned
    .filter(
      (p) =>
        isOnDay(p.datetime, dayKey) &&
        p.status === 'planned' &&
        p.actualWorkoutId === null,
    )
    .sort((a, b) => (a.datetime < b.datetime ? -1 : a.datetime > b.datetime ? 1 : 0));
}

function isOnDay(iso: string, dayKey: string): boolean {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return false;
  return dayKeyUTC(d) === dayKey;
}

function formatKm(km: number): string {
  if (km >= 100) return Math.round(km).toString();
  if (km >= 10) return km.toFixed(1);
  return km.toFixed(2);
}
